import React, {useMemo, useState} from 'react';
import {Box, Button, HStack, Image, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, VStack,} from '@chakra-ui/react';
import {TriangleDownIcon, TriangleUpIcon} from '@chakra-ui/icons';
import {EmptyPage} from "../../../../components/organisms/EmptyPage/EmptyPage";
import {useMutation, useQuery} from "@tanstack/react-query";
import {addFeatured, deleteFeatured, getFeatured} from "../../../../data/api/Customer";
import {getData} from "../../../../utils/Utils";
import {FeaturedModel} from "../../../../data/model/Product";
import {FeaturedDialogContent} from "../../../../components/molecules/ProductCard/ProductCard";
import {useDialogStore} from "../../../../store/useAlertDialog";

interface ImageType {
    id: string;
    url: string;
    mediaType: string;
    isMain: boolean;
    mediaSection: boolean;
    name: string;
    userId: string;
}

interface Category {
    id: string;
    name: string;
    description: string;
    image: ImageType;
}

interface Producer {
    id: string;
    name: string;
}

interface Product {
    id: string;
    name: string;
    labels: string;
    mainImage: string;
    categories: Category[];
    producer: Producer;
    barcode: string;
}

export interface DataItem {
    id: string;
    name: string;
    description: string;
    startDate: number;
    endDate: number;
    chipText?: string;
    product?: Product;
    event?: string;
}

interface SortConfig {
    key: keyof DataItem | "tipologia" | "eventi" | null;
    direction: 'asc' | 'desc';
}

const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const SortableTable: React.FC = () => {
    const {data: response, isLoading, refetch} = useQuery({queryKey: ['featured-table'], queryFn: () => getFeatured()});

    const data = getData<FeaturedModel[]>(response);

    const {showDialog, closeDialog} = useDialogStore();

    const [sortConfig, setSortConfig] = useState<SortConfig>({key: null, direction: 'asc'});

    const {mutateAsync: deleteMutate, isPending: isPendingDelete} = useMutation({
        mutationKey: [],
        mutationFn: ({id}: { id: string }) => deleteFeatured(id),
    });

    const getSortValue = (
        item: FeaturedModel,
        key: keyof DataItem | "tipologia" | "eventi"
    ) => {
        if (key === 'tipologia') {
            return item.product ? "Prodotto" : item.event ? "Eventi" : "";
        }
        if (key === 'eventi') {
            return item.event ? "Eventi" : "";
        }
        return item[key] ?? "";
    };

    const handleSort = (key: keyof FeaturedModel | "tipologia" | "eventi") => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({key, direction});
    };

    const sortedData = useMemo(() => {
        const sortKey = sortConfig.key;
        if (sortKey === null) return data;

        return [...(data || [])].sort((a, b) => {
            const valueA = getSortValue(a, sortKey);
            const valueB = getSortValue(b, sortKey);
            if (typeof valueA === 'number' && typeof valueB === 'number') {
                return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
            }
            return sortConfig.direction === 'asc'
                ? String(valueA).localeCompare(String(valueB))
                : String(valueB).localeCompare(String(valueA));
        });
    }, [data, sortConfig]);

    return (isLoading ? <VStack w={'100%'} h={'100%'} alignItems={'center'} justifyContent={'center'}>
        <Spinner/>
    </VStack> : ((data || []).length > 0) ? <Box p={4} w={'100%'} overflowX="auto">
        <Table variant="simple" minWidth="800px" backgroundColor={'white'}>
            <Thead>
                <Tr>
                    <Th></Th>
                    <Th
                        cursor="pointer"
                        onClick={() => handleSort('tipologia')}
                    >
                        Tipologia{" "}
                        {sortConfig.key === 'tipologia' &&
                            (sortConfig.direction === 'asc' ? <TriangleUpIcon/> : <TriangleDownIcon/>)}
                    </Th>
                    {/*<Th*/}
                    {/*    cursor="pointer"*/}
                    {/*    onClick={() => handleSort('eventi')}*/}
                    {/*>*/}
                    {/*    Eventi{" "}*/}
                    {/*    {sortConfig.key === 'eventi' &&*/}
                    {/*        (sortConfig.direction === 'asc' ? <TriangleUpIcon/> : <TriangleDownIcon/>)}*/}
                    {/*</Th>*/}
                    <Th
                        cursor="pointer"
                        onClick={() => handleSort('name')}
                    >
                        Nome{" "}
                        {sortConfig.key === 'name' &&
                            (sortConfig.direction === 'asc' ? <TriangleUpIcon/> : <TriangleDownIcon/>)}
                    </Th>
                    <Th
                        cursor="pointer"
                        onClick={() => handleSort('description')}
                    >
                        Descrizione{" "}
                        {sortConfig.key === 'description' &&
                            (sortConfig.direction === 'asc' ? <TriangleUpIcon/> : <TriangleDownIcon/>)}
                    </Th>
                    <Th
                        cursor="pointer"
                        onClick={() => handleSort('startDate')}
                    >
                        Inizio{" "}
                        {sortConfig.key === 'startDate' &&
                            (sortConfig.direction === 'asc' ? <TriangleUpIcon/> : <TriangleDownIcon/>)}
                    </Th>
                    <Th
                        cursor="pointer"
                        onClick={() => handleSort('endDate')}
                    >
                        Fine{" "}
                        {sortConfig.key === 'endDate' &&
                            (sortConfig.direction === 'asc' ? <TriangleUpIcon/> : <TriangleDownIcon/>)}
                    </Th>
                    <Th
                        position="sticky"
                        right="0"
                        bg="white"
                        zIndex={1}
                    >
                        Azioni
                    </Th>
                </Tr>
            </Thead>
            <Tbody>
                {sortedData?.map((item) => {
                    const mainImage = item.product !== undefined ? (item.product?.mainImage || item.product?.mediaList?.find(model => model.isMain)?.url) : item?.event?.mediaList?.find(model => model.isMain)?.url;

                    return (
                        <Tr key={item.id}>
                            <Td><Image w={"80px"} objectFit={'contain'} aspectRatio={1} src={mainImage}/></Td>
                            <Td>{item.product ? "Prodotto" : item.event ? "Eventi" : ""}</Td>
                            {/*<Td>{item.event ? "Eventi" : ""}</Td>*/}
                            <Td>{item.name}</Td>
                            <Td>{item.description}</Td>
                            <Td>{formatDate(item.startDate || 0)}</Td>
                            <Td>{formatDate(item.endDate || 0)}</Td>
                            <Td
                                position="sticky"
                                right="0"
                                bg="white"
                            >
                                <VStack alignItems="stretch">
                                    <Button
                                        onClick={() => {
                                            showDialog({
                                                title: "In evidenza",
                                                description: "Inserisci le informazioni da mostrare nel prodotto in evidenza",
                                                cancelButtonText: "Annulla",
                                                onPressCancelButton: () => closeDialog(),
                                                children: (
                                                    <VStack w={'100%'} h={'100%'} alignItems={'stretch'}>
                                                        {item.product &&
                                                            <FeaturedDialogContent item={item?.product || {}}
                                                                                   featuredItem={item}
                                                                                   type={"product"}
                                                                                   close={async ({refetch: refetchBool}) => {
                                                                                       if (refetchBool) await refetch();
                                                                                       closeDialog();
                                                                                   }}/>}
                                                        {item.event && <FeaturedDialogContent item={item?.event || {}}
                                                                                              featuredItem={item}
                                                                                              type={"event"}
                                                                                              close={async ({refetch: refetchBool}) => {
                                                                                                  if (refetchBool) await refetch();
                                                                                                  closeDialog();
                                                                                              }}/>}
                                                    </VStack>
                                                )
                                            });
                                        }}
                                        w={'100%'} size="sm" mr={2}>Modifica</Button>
                                    <Button w={'100%'} size="sm" onClick={() => {
                                        deleteMutate({id: item?.id || ''}).finally(() => {
                                            // close?.({refetch: true}).finally(() => {
                                            //     setButtonLoading(undefined);
                                            // });
                                        });
                                    }} colorScheme="red">Rimuovi</Button>
                                </VStack>
                            </Td>
                        </Tr>
                    )
                })}
            </Tbody>
        </Table>
    </Box> : <EmptyPage stackProps={{
        width: "100%"
    }} title={"Nessun prodotto trovato"}
                        description={"Prova a cambiare i filtri o ad aggiungere nuovi ordini."}/>);
};

function FeaturedPage() {
    return <HStack w={'100%'} h={'100%'}>
        <VStack w={"100%"} h={"100%"} pt={"24px"} alignItems={"flex-start"} overflowX={"hidden"}
                overflowY={"hidden"}>
            <Text fontSize={"24px"} fontWeight={"bold"} ms={"24px"}>In evidenza</Text>

            <SortableTable/>
        </VStack>
    </HStack>
}

export default FeaturedPage;
